import styled, { keyframes } from "styled-components";
import { colors } from "../../../styles/colors";

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const StyledSettingsLoadingSkeleton = styled.div`
  padding: 0 25px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  .loading-sections {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .loading-section {
    margin-bottom: 40px;
    padding: 0 5px;
  }

  .section-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 19px;

    .section-icon {
      width: 20px;
      height: 20px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}30 25%,
        ${colors.blueGray}60 50%,
        ${colors.blueGray}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 4px;
    }

    .section-title {
      width: 120px;
      height: 16px;
      background: linear-gradient(
        90deg,
        ${colors.darkBlue}40 25%,
        ${colors.darkBlue}70 50%,
        ${colors.darkBlue}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 3px;
    }
  }

  .section-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .input-group {
    display: grid;
    grid-template-columns: 85px 1fr auto;
    align-items: center;
    padding: 18px 0;
    border-bottom: 0.5px solid ${colors.lightBlueGray};
    gap: 20px;
    min-width: 0;

    @media (max-width: 768px) {
      gap: 12px;
      padding: 15px 0;
      grid-template-columns: 70px 1fr auto;
    }

    .input-label {
      width: 60px;
      height: 14px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}40 25%,
        ${colors.blueGray}70 50%,
        ${colors.blueGray}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 3px;
      justify-self: end;
    }

    .input-field {
      width: 100%;
      height: 16px;
      background: linear-gradient(
        90deg,
        ${colors.darkBlue}30 25%,
        ${colors.darkBlue}60 50%,
        ${colors.darkBlue}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 3px;
    }

    .input-actions {
      width: 32px;
      height: 32px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}30 25%,
        ${colors.blueGray}60 50%,
        ${colors.blueGray}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 6px;
      justify-self: start;

      @media (max-width: 768px) {
        width: 28px;
        height: 28px;
      }
    }
  }

  .upload-area {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    border: 2px dashed ${colors.lightBlueGray};
    border-radius: 8px;
    background: ${colors.white};

    .upload-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}30 25%,
        ${colors.blueGray}60 50%,
        ${colors.blueGray}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 50%;
      margin-bottom: 12px;
    }

    .upload-text {
      width: 180px;
      height: 16px;
      background: linear-gradient(
        90deg,
        ${colors.darkBlue}40 25%,
        ${colors.darkBlue}70 50%,
        ${colors.darkBlue}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 3px;
      margin-bottom: 6px;
    }

    .upload-subtext {
      width: 140px;
      height: 12px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}30 25%,
        ${colors.blueGray}60 50%,
        ${colors.blueGray}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 2px;
    }
  }

  .textarea-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 20px;

    .textarea-label {
      width: 100px;
      height: 14px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}40 25%,
        ${colors.blueGray}70 50%,
        ${colors.blueGray}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 3px;
    }

    .textarea-field {
      width: 100%;
      height: 60px;
      background: linear-gradient(
        90deg,
        ${colors.white} 25%,
        ${colors.lightBlueGray}30 50%,
        ${colors.white} 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border: 1px solid ${colors.lightBlueGray};
      border-radius: 8px;
    }
  }

  .links-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .link-item {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    padding: 16px 0;
    border-bottom: 0.5px solid ${colors.lightBlueGray};
    gap: 20px;
    min-width: 0;

    .link-content {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 0;

      .link-name {
        width: 100px;
        height: 16px;
        background: linear-gradient(
          90deg,
          ${colors.darkBlue}40 25%,
          ${colors.darkBlue}70 50%,
          ${colors.darkBlue}40 75%
        );
        background-size: 200px 100%;
        animation: ${shimmer} 1.5s infinite;
        border-radius: 3px;
      }

      .link-text {
        width: 100%;
        height: 14px;
        background: linear-gradient(
          90deg,
          ${colors.blueGray}30 25%,
          ${colors.blueGray}60 50%,
          ${colors.blueGray}30 75%
        );
        background-size: 200px 100%;
        animation: ${shimmer} 1.5s infinite;
        border-radius: 2px;
      }
    }

    .link-actions {
      width: 60px;
      height: 32px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}30 25%,
        ${colors.blueGray}60 50%,
        ${colors.blueGray}30 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 6px;
      justify-self: start;
    }
  }

  .warning-subtitle {
    font-size: 12px;
    color: ${colors.blueGray};
    margin-bottom: 19px;
    border: 1px solid #ffcc00;
    width: fit-content;
    margin: 0 auto;
    border-radius: 100px;
    padding: 0 4px 0 9px;
    display: flex;
    align-items: center;
    height: 22px;
    gap: 8px;
    margin-top: 19px;

    .warning-icon {
      width: 12px;
      height: 12px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}40 25%,
        ${colors.blueGray}70 50%,
        ${colors.blueGray}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 2px;
    }

    .warning-text {
      width: 200px;
      height: 12px;
      background: linear-gradient(
        90deg,
        ${colors.blueGray}40 25%,
        ${colors.blueGray}70 50%,
        ${colors.blueGray}40 75%
      );
      background-size: 200px 100%;
      animation: ${shimmer} 1.5s infinite;
      border-radius: 2px;
    }
  }
`;
